import { useState, type FormEvent } from 'react';
import { siteConfig } from '@/mocks/site';

const FORM_ID = 'daimkjboh653ivfvogig';
const SUBMIT_ADDR = 'https://readdy.ai/api/form/daimkjboh653ivfvogig';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function SubscribeCta() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honey = String(formData.get('website_alt') ?? '').trim();
    if (honey) {
      setStatus('success');
      setFormError('');
      return;
    }
    formData.delete('website_alt');

    setStatus('submitting');
    setFormError('');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => body.append(key, String(value)));

      const res = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (res.ok && code === 'OK' && !isSpam) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(
          typeof serverMsg === 'string' && serverMsg ? serverMsg : '提交失败，请稍后再试。',
        );
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，请检查网络后重试。');
    }
  };

  return (
    <section className="bg-background-50">
      <div className="px-4 md:px-8 py-12 md:py-16">
        <div className="rounded-lg bg-primary-950 text-background-50 p-8 md:p-12">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-6">
              <span className="inline-flex items-center gap-2 text-xs tracking-widest text-primary-300 font-label mb-3">
                <i className="ri-notification-3-line"></i>
                NEWSLETTER
              </span>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-background-50 leading-tight">
                订阅独家快讯，第一时间收到新线索
              </h2>
              <p className="mt-3 text-sm text-background-200/80 leading-relaxed max-w-lg">
                每周精选独家首发与时间线更新，只发有进展的事。不灌水，不群发，随时可退订。
              </p>
              <p className="mt-4 text-xs text-background-300/70">
                也可以直接写信到 {siteConfig.email}
              </p>
            </div>

            <div className="lg:col-span-6">
              <form
                id={FORM_ID}
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-background-50/5 border border-background-50/15 rounded-lg p-5 md:p-6"
              >
                <div className="flex flex-col sm:flex-row gap-3">
                  <label className="flex-1">
                    <span className="block text-xs text-background-200/70 mb-2">邮箱地址</span>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="you@example.com"
                      className="w-full h-11 px-3 rounded-md bg-background-50 text-foreground-900 text-sm outline-none placeholder:text-foreground-400"
                    />
                  </label>
                  <label className="sm:w-40">
                    <span className="block text-xs text-background-200/70 mb-2">关注方向</span>
                    <select
                      name="topic"
                      defaultValue="全部栏目"
                      className="w-full h-11 px-3 rounded-md bg-background-50 text-foreground-900 text-sm outline-none cursor-pointer"
                    >
                      <option>全部栏目</option>
                      <option>娱乐现场</option>
                      <option>科技内幕</option>
                      <option>财经风向</option>
                      <option>民生观察</option>
                      <option>生活方式</option>
                    </select>
                  </label>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  className="form-extra-slot"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-2 h-11 px-7 rounded-md bg-accent-600 text-background-50 text-sm font-medium hover:bg-accent-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  <i className="ri-send-plane-fill"></i>
                  {status === 'submitting' ? '提交中…' : '立即订阅'}
                </button>

                {status === 'success' && (
                  <p className="mt-3 text-sm text-primary-300 flex items-center gap-2">
                    <i className="ri-checkbox-circle-fill"></i>
                    订阅成功，感谢你的关注。
                  </p>
                )}
                {status === 'error' && (
                  <p className="mt-3 text-sm text-accent-300 flex items-center gap-2">
                    <i className="ri-error-warning-fill"></i>
                    {formError}
                  </p>
                )}
                <p className="mt-3 text-xs text-background-300/60">
                  提交即表示同意我们通过邮件向你发送内容更新。
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}